import React, { useEffect, useState } from 'react';
import { useArtworksContext } from '../hooks/useArtworksContext';
import { useAuthContext } from '../hooks/useAuthContext';
import { axiosInstance } from '../context/AuthContext';

const ArtworkEditModal = ({ artwork, onClose }) => {
  const { dispatch } = useArtworksContext();
  const { user } = useAuthContext();

  const [title, setTitle] = useState(artwork.title || '');
  const [rpId, setRpId] = useState(artwork.RaspID != null ? String(artwork.RaspID) : '');
  const [galleryId, setGalleryId] = useState(artwork.galleryId || '');
  const [price, setPrice] = useState(
    artwork.price != null ? String(artwork.price) : ''
  );
  const [galleries, setGalleries] = useState([]);
  const [isLoadingGalleries, setIsLoadingGalleries] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const canEditPrice = user?.permissions?.includes('admin');

  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        const response = await axiosInstance.get('/gallery');
        const allGalleries = response.data?.galleries || [];
        const isCuratorOrAdmin =
          user?.permissions?.includes('curator') || user?.permissions?.includes('admin');

        const available = isCuratorOrAdmin
          ? allGalleries
          : allGalleries.filter((g) => user?.galleryId?.includes(g.galleryId));

        setGalleries(available);
      } catch (err) {
        console.error('Error fetching galleries:', err);
      } finally {
        setIsLoadingGalleries(false);
      }
    };

    if (user) fetchGalleries();
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!rpId) {
      setError('Camera ID is required');
      return;
    }
    if (!galleryId) {
      setError('Gallery is required');
      return;
    }

    const payload = {
      title: title.trim(),
      RaspID: Number(rpId),
      galleryId,
    };

    if (canEditPrice) {
      const numericPrice = Number(price);
      if (!Number.isFinite(numericPrice) || numericPrice < 0.5) {
        setError('Price must be at least $0.50');
        return;
      }
      payload.price = numericPrice;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.patch(`/artworks/${artwork._id}`, payload);
      dispatch({ type: 'UPDATE_ARTWORK', payload: response.data });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update artwork');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-artwork-title"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h2 id="edit-artwork-title" className="text-lg font-semibold text-gray-900">
            Edit Artwork
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="edit-title">
              Title
            </label>
            <input
              id="edit-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="edit-camera">
                Camera ID
              </label>
              <input
                id="edit-camera"
                type="number"
                value={rpId}
                onChange={(e) => setRpId(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="edit-price">
                Price (USD)
              </label>
              <input
                id="edit-price"
                type="number"
                min="0.50"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
                required={canEditPrice}
                disabled={!canEditPrice}
                readOnly={!canEditPrice}
              />
              {!canEditPrice && (
                <p className="mt-1 text-[11px] text-gray-500">Only admins can change price</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="edit-gallery">
              Gallery
            </label>
            {isLoadingGalleries ? (
              <div className="text-sm text-gray-500">Loading galleries...</div>
            ) : (
              <select
                id="edit-gallery"
                value={galleryId}
                onChange={(e) => setGalleryId(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                required
              >
                <option value="">Select a gallery</option>
                {galleries.map((gallery) => (
                  <option key={gallery._id} value={gallery.galleryId}>
                    {gallery.displayName}
                  </option>
                ))}
              </select>
            )}
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ArtworkEditModal;
